<template>
  <div ref="chartContainer" class="pie-chart-container"></div>
</template>

<script setup>
/* global defineProps */
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import * as echarts from 'echarts';

const props = defineProps({
  data: {
    type: Array,
    required: true,
    default: () => []
  },
  colors: {
    type: Array,
    required: true,
    default: () => ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de']
  },
  suffix: {
    type: String,
    default: ''
  },
  dimmedNames: {
    type: Array,
    default: () => []
  }
});

const chartContainer = ref(null);
let chart = null;

const initChart = () => {
  // 重新初始化以避免容器尺寸/数据变化导致的残留状态
  const container = chartContainer.value;
  if (!container) return;

  if (chart) {
    chart.dispose();
    chart = null;
  }

  chart = echarts.init(container);

  const option = {
    color: props.colors,
    tooltip: {
      trigger: 'item',
      formatter: params => {
        // tooltip 加上单位后缀
        return `${params.marker} ${params.name}：${params.value} ${props.suffix}`.trim();
      }
    },
    series: [{
      type: 'pie',
      center: ['50%', '35%'],
      radius: '60%',
      label: {
        show: true,
        position: 'outer',
        distanceToLabelLine: 10,
        formatter(params) {
          // 百分比四舍五入展示
          return `${Math.round(params.percent)}%`;
        }
      },
      labelLine: {
        show: true,
        length: 10,
        length2: 10
      },
      data: props.data,
      itemStyle: {
        borderColor: '#fff',
        borderWidth: 2
      }
    }]
  };

  chart.setOption(option);
};

const updateChart = () => {
  if (!chart) return;

  const seriesData = props.data.map((item) => {
    const isDimmed = props.dimmedNames.includes(item.name);
    return {
      value: item.value,
      name: item.name,
      itemStyle: {
        opacity: isDimmed ? 0.1 : 1 // Dim the segment if it's in dimmedNames
      },
      label: {
        show: !isDimmed, // Do not show label for dimmed segments
        fontWeight: isDimmed ? 'normal' : 'bold' // Bold font for non-dimmed segments
      }
    };
  });

  chart.setOption({
    series: [{
      data: seriesData
    }]
  });
};

watch(
  () => props.data,
  initChart,
  { deep: true }
);

watch(
  () => props.colors,
  initChart,
  { deep: true }
);

watch(() => props.data, updateChart);
watch(() => props.dimmedNames, updateChart);

onMounted(() => {
  initChart();
});

onBeforeUnmount(() => {
  if (chart) {
    chart.dispose();
    chart = null;
  }
});
</script>

<style scoped>
.pie-chart-container {
  width: 100%;
  height: 300px;
}
</style>
